import React, {useEffect, useState} from 'react'
import Translate from "../../../components/Translate";
import ChevronRightIcon from "../../../assets/icons/ChevronRightIcon";
import Colors from "../../../utils/Colors";
import './StatisticCard.css';
import ForumIcon from "../../../assets/icons/ForumIcon";
import Tooltip from '../Tooltip';

const ICON_SIZE = 18;

// full number in the title attribute, compacted only past 100k so tiles never wrap
const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return {text: '—', title: ''};

    if (typeof value !== 'number' || !isFinite(value)) return {text: value, title: ''};

    const full = value.toLocaleString('en-US');
    const abs = Math.abs(value);

    if (abs >= 1000000) return {text: (value / 1000000).toFixed(1) + 'M', title: full};
    if (abs >= 100000) return {text: Math.round(value / 1000) + 'K', title: full};

    return {text: full, title: ''};
};

function StatisticCard({
                           title,
                           value,
                           loading,
                           className,
                           onClick,
                           delta,
                           icon = <ForumIcon style={{color: Colors.blueDark}}/>,
                           iconBare = false
                       }) {

    // distinguishes "never loaded" (skeleton) from "refetching" (hold the old number)
    const [everLoaded, setEverLoaded] = useState(false);

    useEffect(() => {
        if (!loading) setEverLoaded(true);
    }, [loading]);

    const clickable = typeof onClick === 'function';

    const handleKeyDown = (e) => {
        if (!clickable) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(e);
        }
    };

    const renderIcon = () => {
        if (icon === 'noIcon') return null;

        /*
         * A bare mark keeps its own size and stands in no box - a status dot is
         * 8px and means nothing at 18, and the bordered square around it reads
         * as a second thing on the tile rather than as the dot's frame.
         */
        if (iconBare && React.isValidElement(icon)) return icon;

        const iconStyle = {width: ICON_SIZE, height: ICON_SIZE};

        if (typeof icon === 'string') {
            return <img style={iconStyle} width={ICON_SIZE} height={ICON_SIZE} src={icon} alt=""/>;
        }

        if (React.isValidElement(icon)) {
            return React.cloneElement(icon, {style: iconStyle, width: ICON_SIZE, height: ICON_SIZE});
        }

        if (typeof icon === 'function') {
            const IconComponent = icon;
            return <IconComponent style={iconStyle} width={ICON_SIZE} height={ICON_SIZE}/>;
        }

        return null;
    };

    const renderValue = () => {
        if (loading && !everLoaded) {
            return <div className="stat-tile__skeleton"/>;
        }

        const {text, title: fullValue} = formatValue(value);

        // words and durations ("Unlimited", "00:12:45", "N/A") don't fit at the
        // figure size — only real numbers get it
        const isFigure = typeof value === 'number' && isFinite(value);

        return (
            <Tooltip inRightSide text={fullValue} needTranslate={false} disabled={!fullValue}>
                <div
                className={'stat-tile__value'
                    + (isFigure ? '' : ' stat-tile__value--text')
                    + (loading ? ' stat-tile__value--stale' : '')}
                >
                    {text}
                </div>
            </Tooltip>
        );
    };

    const renderDelta = () => {
        if (!delta || typeof delta.value !== 'number') return null;

        const upIsGood = delta.upIsGood !== false;
        const direction = delta.value > 0 ? 'up' : delta.value < 0 ? 'down' : 'flat';
        const tone = direction === 'flat'
            ? 'flat'
            : (direction === 'up') === upIsGood ? 'up' : 'down';

        const sign = delta.value > 0 ? '+' : '';

        return (
            <div className={'stat-tile__delta stat-tile__delta--' + tone}>
                <span>{sign}{delta.value}{delta.unit || '%'}</span>
                {delta.period && (
                    <span className="stat-tile__delta-period"><Translate text={delta.period}/></span>
                )}
            </div>
        );
    };

    const classNames = [
        'stat-tile',
        clickable ? 'stat-tile--clickable' : '',
        className || ''
    ].filter(Boolean).join(' ');

    const iconElement = renderIcon();

    return (
        <div
            className={classNames}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
        >
            <div className="stat-tile__head">
                {iconElement && (
                    <span className={'stat-tile__icon' + (iconBare ? ' stat-tile__icon--bare' : '')}>
                        {iconElement}
                    </span>
                )}
                <span className="stat-tile__label"><Translate text={title}/></span>
                {clickable && (
                    <span className="stat-tile__go">
                        <ChevronRightIcon width={16} height={16} style={{color: Colors.silverBlue}}
                                          stroke="currentColor"/>
                    </span>
                )}
            </div>

            {renderValue()}
            {renderDelta()}
        </div>
    );
}

export default StatisticCard;



// WEBPACK FOOTER //
// ./src/components-new/general/statistics/StatisticCard.jsx